import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { HeaderPrimary } from "../../components/HeaderText/HeaderText.style";
import { authService } from "../../services/Services";
import { ErrorMessage } from "../../types/Error";
import "./ForgotPassword.css";

export default function ForgotPassword() {
    const [username, setUsername] = useState("");
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const navigate = useNavigate();

    const validateForm = () => {
        return username.length > 0;
    };

    const handleSendCode = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        setFormError(null);

        try {
            await authService.forgotPassword(username);
            navigate("/reset-password", { state: { username } });
        } catch (error) {
            const message = error instanceof Error ? error.message : "Something went wrong";
            // Show known user-facing errors inline; escalate unexpected errors globally
            const inlineErrors: string[] = [ErrorMessage.InvalidUsername, ErrorMessage.TooManyAttempts];
            if (error instanceof Error && inlineErrors.includes(error.message)) {
                setFormError(message);
            } else {
                setFormError("Something went wrong. Please try again later.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="ForgotPasswordContainer">
            <form onSubmit={handleSendCode} className="ForgotPasswordForm">
                <HeaderPrimary>Forgot Password</HeaderPrimary>
                <Typography variant="body2" sx={{ textAlign: "center", mt: 2, mb: 3 }}>
                    Enter your username and we'll send you a code to reset your password.
                </Typography>
                {formError && (
                    <Alert severity="error" sx={{ mb: 2, maxWidth: 300, mx: "auto" }}>
                        {formError}
                    </Alert>
                )}
                <Box display="flex" justifyContent="center">
                    <TextField
                        label="Username"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        fullWidth
                        sx={{ maxWidth: 300 }}
                    />
                </Box>
                <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        sx={{
                            bgcolor: "#043c4a",
                            height: "50px",
                            minWidth: "150px",
                            color: "#ffffff",
                            ":hover": {
                                bgcolor: "#043c4a",
                            },
                        }}
                        disabled={!validateForm() || loading}
                        type="submit"
                    >
                        {loading ? "Sending..." : "Send Code"}
                    </Button>
                </Box>
                <Box sx={{ mt: 3, textAlign: "center" }}>
                    <Typography variant="body2">
                        Remember your password? <Link to="/login">Back to Login</Link>
                    </Typography>
                </Box>
            </form>
        </Container>
    );
}
